import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table';
import { BarChart2, ChevronDown, ChevronRight, TableIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';

import { useGetClassCapacity } from '~entities/classes/api/get-class-capacity';
import type { ClassCapacityItem } from '~entities/classes/api/get-class-capacity';

import { DataTableColumnHeader } from '~shared/components/table/header';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~shared/shadcn/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

interface CapacityChartProps {
  sessionId: string;
}

const columnHelper = createColumnHelper<ClassCapacityItem>();

const classColumnHelper = createColumnHelper<{
  className: string;
  classId: string;
  registered: number;
  maxSize: number;
  studioId: string;
}>();

const classColumns = [
  classColumnHelper.accessor('className', {
    header: 'Class Name',
    cell: (info) => {
      const value = info.getValue();
      const classId = info.row.original.classId;
      return (
        <Button variant='link' asChild>
          <Link to='/class-analytics/$classId' params={{ classId }}>
            {value}
          </Link>
        </Button>
      );
    },
  }),
  classColumnHelper.accessor('registered', {
    header: 'Registered',
  }),
  classColumnHelper.accessor('maxSize', {
    header: 'Capacity',
  }),
];

const NestedClassesTable = ({
  classes,
}: {
  classes: Array<{
    className: string;
    classId: string;
    registered: number;
    maxSize: number;
    studioId: string;
  }>;
}) => {
  const table = useReactTable({
    data: classes,
    columns: classColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table>
      <TableHeader className='sticky top-0 bg-muted/50'>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className={
                  cell.column.id === 'className'
                    ? 'min-w-[200px]'
                    : 'min-w-[100px]'
                }
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const CapacityTable = ({
  data,
  grouping,
}: {
  data: Array<ClassCapacityItem>;
  grouping: 'className' | 'groupName';
}) => {
  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('name', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Name' />
        ),
        cell: (info) => {
          const row = info.row.original;
          if (grouping === 'className') {
            const classId = row.classes[0]?.classId;
            if (classId) {
              return (
                <Button variant='link' asChild>
                  <Link to='/class-analytics/$classId' params={{ classId }}>
                    {info.getValue()}
                  </Link>
                </Button>
              );
            }
          }
          return info.getValue();
        },
      }),
      columnHelper.accessor('capacity', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Capacity' />
        ),
      }),
      columnHelper.accessor('registered', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Registered' />
        ),
      }),
      columnHelper.accessor('seatsAvailable', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Available' />
        ),
      }),
      columnHelper.accessor('seatAvailabilityPercent', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Fill Rate' />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div>{((1 - value) * 100).toFixed(1)}%</div>;
        },
      }),
    ];
    if (grouping === 'groupName') {
      return [
        columnHelper.display({
          id: 'expander',
          cell: ({ row }) =>
            row.getCanExpand() ? (
              <Button
                variant='ghost'
                onClick={row.getToggleExpandedHandler()}
                className='p-0 hover:bg-transparent'
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className='size-4' />
                ) : (
                  <ChevronRight className='size-4' />
                )}
              </Button>
            ) : null,
          enableHiding: true,
        }),
        ...baseColumns,
      ];
    }
    return baseColumns;
  }, [grouping]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(grouping === 'groupName'
      ? {
          getExpandedRowModel: getExpandedRowModel(),
          getRowCanExpand: (row: Row<ClassCapacityItem>) =>
            Array.isArray(row.original.classes) &&
            row.original.classes.length > 0,
          state: {
            columnVisibility: {
              expander: true,
            },
          },
        }
      : {}),
  });

  return (
    <div className='rounded-md border'>
      <div className='max-h-[500px] overflow-auto'>
        <Table>
          <TableHeader className='sticky top-0 bg-background'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const typedRow = row;
              return (
                <>
                  <TableRow key={typedRow.id}>
                    {typedRow.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className='min-w-[100px]'>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {grouping === 'groupName' && typedRow.getIsExpanded() && (
                    <TableRow>
                      <TableCell colSpan={columns.length} className='p-0'>
                        <div className='bg-muted/50 px-4 py-2'>
                          <div className='max-h-[300px] overflow-auto'>
                            <NestedClassesTable
                              classes={typedRow.original.classes}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export function CapacityChart({ sessionId }: CapacityChartProps) {
  const [showTable, setShowTable] = useState(false);
  const [grouping, setGrouping] = useState<'className' | 'groupName'>(
    'className',
  );

  const { data: capacityData, isLoading } = useGetClassCapacity({
    sessionId,
    groupBy: grouping,
  });

  const chartData = useMemo(() => {
    return (
      capacityData
        ?.map((item: ClassCapacityItem) => ({
          name: item.name,
          available: item.seatsAvailable,
          capacity: item.capacity,
          registered: item.registered,
          fillRate: `${((1 - item.seatAvailabilityPercent) * 100).toFixed(1)}%`,
        }))
        .sort((a, b) => b.available - a.available)
        .slice(0, 10) ?? []
    );
  }, [capacityData]);

  return (
    <Card className='col-span-1 flex h-full flex-col'>
      <CardHeader className='flex flex-col space-y-2 sm:flex-row sm:items-start sm:justify-between sm:space-y-0'>
        <div>
          <CardTitle>Under-filled Classes</CardTitle>
          <CardDescription>Classes with most available spots</CardDescription>
        </div>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
          <Select
            value={grouping}
            onValueChange={(value: 'className' | 'groupName') => {
              setGrouping(value);
            }}
          >
            <SelectTrigger className='w-full sm:w-[180px]'>
              <SelectValue placeholder='Select grouping' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='groupName'>By Group Name</SelectItem>
              <SelectItem value='className'>By Class Name</SelectItem>
            </SelectContent>
          </Select>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  size='icon'
                  onClick={() => {
                    setShowTable(!showTable);
                  }}
                >
                  {showTable ? (
                    <BarChart2 className='size-4' />
                  ) : (
                    <TableIcon className='size-4' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Switch to {showTable ? 'chart' : 'table'} view</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-hidden'>
        {isLoading ? (
          <div className='flex h-[200px] items-center justify-center'>
            Loading...
          </div>
        ) : !showTable ? (
          <ChartContainer
            config={{
              available: {
                label: 'Available Spots',
                color: 'hsl(var(--chart-2))',
              },
            }}
            className='h-[250px] w-full overflow-hidden sm:h-[300px]'
          >
            {/* <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              barSize={30}
              layout='vertical'
            >
              <XAxis
                type='number'
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                dataKey='name'
                type='category'
                axisLine={false}
                tickLine={false}
                width={80}
                tick={{ fontSize: 12 }}
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
                cursor={{ fill: 'transparent' }}
              />
              <Bar
                dataKey='available'
                fill='var(--color-available)'
                radius={[0, 4, 4, 0]}
              />
            </BarChart> */}
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              barSize={30}
            >
              <XAxis
                dataKey='name'
                axisLine={false}
                tickLine={false}
                padding={{ left: 10, right: 10 }}
                angle={-35}
                textAnchor='end'
                height={50}
                interval={0}
                tick={{ fontSize: 11 }}
                tickMargin={5}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                padding={{ top: 10 }}
                tick={{ fontSize: 12 }}
                type='number'
                // tickFormatter={(value: number) => `${value.toFixed(1)}%`}
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
                cursor={{ fill: 'transparent' }}
              />
              <Bar
                dataKey='available'
                fill='var(--color-available)'
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <div className='max-h-[calc(100vh-400px)] overflow-auto'>
            <CapacityTable data={capacityData ?? []} grouping={grouping} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
